import { ReactElement, useMemo } from "react";
import { ItemResponsiveBehavior } from "../CollapsibleList/CollapsibleList";
import { ToolbarItem } from "./ToolbarItem";
import { Toggle } from "../Toggle/Toggle";
import { ActionListCheckbox } from "../ActionList/ActionListCheckbox";
import { ToolbarPriority } from "./Toolbar";

export type ToolbarToggleProps = {
	value: boolean;
	onChange: (value: boolean) => void;
	trackingId: string;
	isDisabled?: boolean;
	text: string;
	tooltip?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
	shouldAutoCloseResponsiveDropOver?: boolean;
};

export const ToolbarToggle = (props: ToolbarToggleProps): ReactElement => {
	const {
		value,
		onChange,
		trackingId,
		isDisabled,
		text,
		tooltip,
		responsiveBehavior,
		shouldAutoCloseResponsiveDropOver = false
	} = props;

	const renderNormalMode = (
		ref: (element: HTMLElement | null) => void,
		onKeyDown: (args: React.KeyboardEvent<HTMLElement>) => void,
		tabIndex: number
	): ReactElement => {
		return (
			<Toggle
				ref={ref}
				onKeyDown={onKeyDown}
				tabIndex={tabIndex}
				value={value}
				onChange={onChange}
				isDisabled={isDisabled}
				trackingId={trackingId}
				text={text}
				tooltip={tooltip}
			/>
		);
	};

	const renderCollapsedMode = (): ReactElement => {
		return (
			<ActionListCheckbox
				value={value}
				onChange={onChange}
				text={text}
				isDisabled={isDisabled}
				trackingId={trackingId}
				shouldNotAutoCloseContainingDropOver={!shouldAutoCloseResponsiveDropOver}
			/>
		);
	};

	const priority = useMemo(
		(): number => {
			if (responsiveBehavior === "hide") {
				return ToolbarPriority.LowPriorityItem;
			}

			return ToolbarPriority.Default;
		},
		[responsiveBehavior]
	);

	return (
		<ToolbarItem
			priority={priority}
			responsiveBehavior={responsiveBehavior}
			isDisabled={isDisabled}
			renderNormalMode={renderNormalMode}
			renderCollapsedMode={() => renderCollapsedMode()}
		/>
	);
};
ToolbarToggle.displayName = "Toolbar.Toggle";